import classNames from 'classnames'
import type { FunctionComponent } from 'react'

import { Container, Flex } from '../../components/layout/grid'
import { Markdown } from '../../components/markdown'
import { Desktop, Mobile } from '../../components/responsive'
import { ResponsiveImage } from '../../components/responsive-image'
import { TokenIcon } from '../../components/token-icon'
import type { BackgroundImageSectionFragment } from '../../graphql/fragments/BackgroundImageSectionFragment.graphql'
import { ActionButton } from '../action-button'
import styles from './background-image-section.module.scss'

const TOKEN_SCALES = [1.75, 1.5, 1]

/**
 * Large headline with a background image. Tokens are applied to the top left
 * and top right of the headline and are themed.
 */
export const BackgroundImageSection: FunctionComponent<Props> = ({
    heading,
    headingAlign = 'system',
    headingSize = 'default',
    backgroundImage,
    includeCutout = true,
    button,
    copy,
    copyAlign = 'left',
    icons = [],
}) => {
    const headingClass = headingSize === 'large' ? 'heading-5xl' : 'heading-4xl'

    const headings: string[] = heading?.split(/\\n/g).slice(0, 3) ?? [] // split escaped newlines from contentful
    const headingAlignment = alignmentTuple(headingAlign, headings.length)

    const copyAlignment = getCopyAlignment(copyAlign as TCopyAlignment)

    const tokenAnchors = icons.map((tokenType, tokenIndex: number) => (
        <TokenIcon
            key={`${tokenType}--${tokenIndex}`}
            type={tokenType}
            color='primary'
            className={styles[`anchor-${tokenIndex}`]}
            scale={TOKEN_SCALES[tokenIndex]}
            drift={0}
        />
    ))

    return (
        <div
            className={classNames(styles.wrapper, {
                ['stairs--top-left']: includeCutout,
            })}
        >
            <ResponsiveImage
                src={backgroundImage?.source2x ?? ''}
                alt={backgroundImage?.alt}
                className={styles.background}
            />
            <Container className={styles.container}>
                <div className={styles['heading-section']}>
                    <Desktop>
                        <div className={styles.relative}>
                            {headings.map((text, index) => (
                                <Flex
                                    justify={headingAlignment[index]}
                                    key={index}
                                >
                                    <div style={{ position: 'relative' }}>
                                        <Markdown
                                            tag='div'
                                            typeStyle={headingClass}
                                            markdown={text.trim()}
                                        />
                                    </div>
                                </Flex>
                            ))}
                            {tokenAnchors}
                        </div>
                    </Desktop>
                    <Mobile>
                        <Flex justify='center' align='flex-end'>
                            <div className={styles.relative}>
                                {tokenAnchors}
                                <Markdown
                                    tag='div'
                                    typeStyle='heading-md'
                                    markdown={heading}
                                />
                            </div>
                        </Flex>
                    </Mobile>
                </div>
                <div className={styles['copy-block-container']}>
                    <Flex
                        justify='center'
                        align={copyAlignment}
                        direction='column'
                    >
                        <Flex
                            justify='center'
                            align={copyAlignment}
                            direction='column'
                            className={styles['copy-block']}
                        >
                            <Desktop visible={!!copy}>
                                <Markdown
                                    markdown={copy}
                                    typeStyle='body-2xl'
                                />
                            </Desktop>
                            <Mobile visible={!!copy}>
                                <Markdown markdown={copy} typeStyle='body-lg' />
                            </Mobile>
                            {!!button && (
                                <div
                                    className={classNames(
                                        styles[`button-container`],
                                        !copy &&
                                            styles[
                                                `button-container-no-copy-above`
                                            ]
                                    )}
                                >
                                    <ActionButton
                                        {...button}
                                        type={button.type as any}
                                        placement='section'
                                        size='medium'
                                    />
                                </div>
                            )}
                        </Flex>
                    </Flex>
                </div>
            </Container>
        </div>
    )
}

export default BackgroundImageSection

type AlignmentTuple =
    | ['center']
    | ['flex-start', 'flex-end']
    | ['flex-start', 'flex-end', 'center']
    | 'center'[]

function alignmentTuple(align: string, length: number): AlignmentTuple {
    if (align === 'center') {
        return Array(length).fill('center')
    }

    switch (length) {
        case 1:
            return ['center']
        case 2:
            return ['flex-start', 'flex-end']
        case 3:
            return ['flex-start', 'flex-end', 'center']
        default:
            // Fallback case
            return Array(length).fill('center')
    }
}

type TCopyAlignment = 'left' | 'center' | 'right'

function getCopyAlignment(align: TCopyAlignment) {
    switch (align) {
        case 'center':
            return 'center'
        case 'right':
            return 'flex-end'
        case 'left':
        default:
            return 'flex-start'
    }
}

type Props = Omit<BackgroundImageSectionFragment, 'sys' | 'type'>
